<template>
  <div class="overlay">
    <div
      v-if="teamStore.currentTeam"
      :style="{ '--max-team-amount': maxTeamAmount, '--el-height': elHeight }"
      class="overlay-wrapper"
    >
      <TeamItem
        :team="teamStore.currentTeam"
        class="overlay__team overlay__team--current"
        @click="showSettings"
      />
      <TeamItem
        v-for="client in sortTeamByName(teamStore.currentTeam.clients)"
        :key="`current-client-${client.id}`"
        :team="client"
        class="overlay__team overlay__team--current-client"
        @click="selectTeam(client)"
      />
      <template
        v-for="team in sortTeamByName(otherTeams)"
        :key="team.id"
      >
        <TeamItem
          :team="team"
          class="overlay__team overlay__team--other"
          @click="selectTeam(team)"
        />
        <TeamItem
          v-for="client in sortTeamByName(team.clients)"
          :key="`client-${client.id}`"
          :team="client"
          class="overlay__team overlay__team--other-client"
          @click="selectTeam(client)"
        />
      </template>
    </div>
    <div class="overlay__actions">
      <SettingsItem class="overlay__settings" />
      <DarkModeItem class="overlay__dark-mode" />
      <div
        class="overlay__logout"
        @click="logout"
      >
        <Icon
          class="overlay__logout__icon"
          name="duotone-signout"
        />
        <div class="overlay__logout__text">Log Out</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { useRouter } from 'vue-router'
import { useToast } from '@/uiKit/Toast/useToast'
import { useAuthStore } from '@/modules/Auth/useAuthStore'
import { useTeamStore } from '@/pinia/useTeamStore'
import type { TeamPayload } from '@/store/types'
import Icon from '@/uiKit/Icon/Icon.vue'
import { errorsByCode } from '@/backend/error'
import { ErrorCodes } from '@/backend/error/errors'

import DarkModeItem from './SidebarOverlay/DarkModeItem.vue'
import SettingsItem from './SidebarOverlay/SettingsItem.vue'
import TeamItem from './SidebarOverlay/TeamItem.vue'

const emit = defineEmits<{ (e: 'close'): void }>()
const authStore = useAuthStore()
const router = useRouter()
const toast = useToast()

const maxTeamAmount: number = 5
const elHeight: string = '61px'

const teamStore = useTeamStore()

const otherTeams = computed((): TeamPayload[] =>
  teamStore.teams.filter((team) => team.id !== teamStore.currentTeam?.id),
)

const sortTeamByName = (teams: TeamPayload[]): TeamPayload[] =>
  [...teams].sort((a: TeamPayload, b: TeamPayload) => a.name.localeCompare(b.name))

const showSettings = (): void => {
  router.push({ query: { ...router.currentRoute.value.query, settings: 'team' } })
}

const selectTeam = async (team: TeamPayload): Promise<void> => {
  if (team.disabled) {
    toast.warning({
      meta: { title: errorsByCode[ErrorCodes.TEAM_DISABLED] },
    })
    return
  }

  const r = await authStore.selectTeam({ team_id: team.id })

  if ('error' in r) {
    if (r.error.code === ErrorCodes.NOT_AUTHENTICATED) {
      await authStore.logout()
    }

    toast.warning({
      meta: { title: r.error.message.toString() },
    })
    return
  }

  router.push({ name: 'Root' })
  emit('close')
}

const logout = async (): Promise<void> => {
  await authStore.logout()
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/shared/scrollbar';

$borderColor: var(--border);

.overlay {
  background: var(--color-blanket-default);
  position: absolute;
  left: 10px;
  top: 65px;
  width: 260px;
  height: auto;
  border-radius: 10px;
  background: var(--color-surface-popover);
  box-shadow: $effectShadowsMD;
  border: 1px solid var(--color-border-subtle);
  overflow: auto;
  cursor: pointer;
  z-index: var(--workview-center-main-area-popup-menu);
}

.overlay-wrapper {
  @include scrollbar;
  display: block;
  overflow-y: scroll;
  overflow-x: hidden;

  min-height: var(--el-height);
  height: calc((var(--el-height) * var(--max-team-amount)) + (var(--el-height) / 2));
  max-height: 50vh;
  z-index: 1;
  padding: 4px;
}

.overlay__actions {
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: var(--color-surface-popover);
  z-index: 2;
  padding: 4px;
  border-top: 1px solid var(--color-border-subtle);
}

.overlay__team--current-client,
.overlay__team--other-client {
  padding: 10px 28px;
  position: relative;
}

.overlay__team--current-client:before,
.overlay__team--other-client:before {
  content: '';
  display: block;
  width: 3px;
  height: calc(100% - 20px);
  position: absolute;
  left: 16px;
  background: var(--color-border-default);
}

.overlay__logout {
  @include row;
  align-items: center;
  padding: 6px 6px;
  border-radius: 6px;

  &:hover {
    background-color: var(--color-background-transparent-hovered);
  }
  &__icon {
    margin-right: 8px;
    color: var(--color-icon-subtle);
  }

  &__text {
    @include typography(md-1, headlines);
    @include typographyRegularBody200;
    color: var(--color-text-default);
  }
}
</style>
